import React, { useState, useMemo } from 'react';

// --- Type Definitions ---

type FAQItem = {
  id: number;
  category: string;
  question: string;
  answer: string;
};

// --- Dummy Data ---
// A comprehensive list of common questions
const allFAQs: FAQItem[] = [
  { id: 1, category: 'Order Status', question: 'How can I track my order?', answer: 'You can track your order status by clicking on the "Track Order" link in the header and entering your Order ID. You will receive real-time updates on your shipment.' },
  { id: 2, category: 'Order Status', question: 'How long does it take to deliver the order?', answer: 'Delivery times vary based on product availability, your location, and the chosen shipping method. Typically, deliveries are completed within 3-7 business days. For precise estimates, please check the delivery date provided at checkout.' },
  { id: 3, category: 'Delivery/Returns', question: 'I missed the delivery of my order today. What should I do?', answer: 'If you missed your delivery, the courier will usually attempt re-delivery on the next business day. You can also contact the designated courier service directly using the tracking number provided to reschedule a convenient delivery time.' },
  { id: 4, category: 'Payment', question: 'Do you take orders over the phone?', answer: 'Yes, absolutely! We welcome phone orders. To place your order, please call our dedicated sales line at +91 9650660070. Our calling hours are Monday to Sunday, from 8:00 AM to 8:00 PM.' },
  { id: 5, category: 'Delivery/Returns', question: 'Can I accept the package after checking the contents inside it?', answer: 'Due to standard courier policies, you are generally required to accept the package before opening it. If you discover any issues or discrepancies after opening, please refer to our hassle-free return and refund policy.' },
  { id: 6, category: 'Order Status', question: 'How do I know if my order is confirmed?', answer: 'Upon successful completion of your payment, you will instantly receive an Order Confirmation Email and an SMS notification containing your unique Order ID. These confirmations signify that your order has been successfully placed.' },
  { id: 7, category: 'Delivery/Returns', question: 'How can I get my order delivered faster?', answer: 'For urgent deliveries, you can often opt for our Express Delivery service during the checkout process. This option is subject to availability for your specific product and delivery location and may incur additional charges.' },
  { id: 8, category: 'Payment', question: 'Are there any hidden charges on products?', answer: 'We believe in complete transparency. All prices displayed on our website are final and fully inclusive of all applicable taxes. There are no hidden charges. Any separate shipping costs will be clearly indicated during checkout.' },
  { id: 9, category: 'Account/Technical', question: 'How to reset my password?', answer: 'To reset your password, navigate to the "Login" section and click on the "Forgot Password" link. A secure password reset link will then be sent to your registered email address.' },
  { id: 10, category: 'Payment', question: 'What payment options are available?', answer: 'We offer a wide range of convenient payment methods, including major Credit Cards, Debit Cards, Net Banking from various banks, UPI (Unified Payments Interface), and popular mobile wallets.' },
  { id: 11, category: 'Technical Support', question: 'My product is not working. What should I do?', answer: 'If your product is not functioning correctly, please first refer to the user manual. If the issue persists, contact our technical support team through our "Help Center" or by calling our customer service number for troubleshooting assistance.' },
  { id: 12, category: 'Account/Technical', question: 'How can I update my shipping address?', answer: 'You can update your shipping address by logging into your account, going to "My Profile" or "Addresses" section, and making the necessary changes. Please ensure updates are made before placing new orders.' },
  { id: 13, category: 'General', question: 'What are your customer service hours?', answer: 'Our customer service team is available to assist you from Monday to Saturday, 9:00 AM to 7:00 PM (IST). You can reach us via phone, email, or live chat.' },
  { id: 14, category: 'Returns/Refunds', question: 'What is your return policy?', answer: 'We offer a 7-day hassle-free return policy for most products. Items must be unused, in original packaging, and with all tags intact. Please check the specific product page for exact return eligibility.' },
  { id: 15, category: 'Returns/Refunds', question: 'How long does a refund take?', answer: 'Once your returned item is received and inspected, refunds are typically processed within 5-7 business days to your original payment method. You will receive an email notification once the refund is initiated.' },
  { id: 16, category: 'Product Information', question: 'How do I find product specifications?', answer: 'Detailed product specifications, features, and technical data can be found on each product\'s dedicated page under the "Specifications" or "Details" tab.' },
  { id: 17, category: 'Order Status', question: 'Can I change my order after it has been placed?', answer: 'Changes to an order can only be made if it has not yet been processed for shipping. Please contact our customer service immediately with your Order ID to check if modifications are possible.' },
  { id: 18, category: 'Payment', question: 'Is my payment information secure?', answer: 'Yes, we use advanced encryption and security protocols (SSL) to ensure that all your payment information is completely secure and protected during transactions.' },
  { id: 19, category: 'General', question: 'Do you offer bulk discounts?', answer: 'Yes, for bulk orders or business inquiries, please visit our "Request for Quotes" section or contact our sales team directly for special pricing and offers.' },
  { id: 20, category: 'Account/Technical', question: 'Why am I not receiving emails from you?', answer: 'Please check your spam or junk folder first. If you still don\'t receive emails, ensure that your registered email address is correct in your account profile or contact customer support.' },
  { id: 21, category: 'Delivery/Returns', question: 'What if I receive a damaged product?', answer: 'If you receive a damaged product, please do not accept it from the courier. If accepted, take photos immediately and contact customer service within 24 hours for a replacement or refund.' },
  { id: 22, category: 'Product Information', question: 'How can I leave a review for a product?', answer: 'You can leave a review by going to the product page of the item you purchased and clicking on "Write a Review." You can also access this feature from your past orders in your account.' },
  { id: 23, category: 'General', question: 'Where are your physical stores located?', answer: 'Currently, we operate primarily as an online e-commerce platform. We do not have physical retail stores. All purchases are made through our website.' },
  { id: 24, category: 'Technical Support', question: 'My login is not working. What to do?', answer: 'Please ensure you are using the correct email and password. If the issue persists, try resetting your password using the "Forgot Password" link. If all else fails, contact our support team.' },
  { id: 25, category: 'Payment', question: 'Can I pay with EMI?', answer: 'Yes, we offer EMI options through select banking partners for eligible products and order values. Details regarding EMI plans are available during the checkout process.' },
];

/**
 * Accordion Item Component for FAQ
 */
const FAQAccordionItem: React.FC<{ faq: FAQItem, isActive: boolean, onClick: () => void }> = ({ faq, isActive, onClick }) => (
  <div className="border-b border-gray-200">
    <button
      className="flex justify-between items-center w-full py-4 text-left text-lg font-medium text-gray-800 hover:text-blue-600 transition duration-150"
      onClick={onClick}
    >
      {faq.question}
      <svg
        className={`w-5 h-5 transition-transform duration-300 ${isActive ? 'transform rotate-180 text-blue-800' : 'text-gray-500'}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
      </svg>
    </button>
    {isActive && (
      <div className="pb-4 pt-1 pr-6 text-gray-800 text-base leading-relaxed animate-in fade-in slide-in-from-top-4 duration-300">
        {faq.answer}
      </div>
    )}
  </div>
);

// --- Main Component ---

const FAQPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [openFAQId, setOpenFAQId] = useState<number | null>(null);

  // Get unique categories from the data
  const categories = useMemo(() => {
    const uniqueCategories = new Set(allFAQs.map(faq => faq.category));
    return ['All', ...Array.from(uniqueCategories)];
  }, []);

  // Filter FAQs based on search term and category
  const filteredFAQs = useMemo(() => {
    return allFAQs
      .filter(faq => {
        // Filter by category
        const categoryMatch = selectedCategory === 'All' || faq.category === selectedCategory;

        // Filter by search term (case-insensitive)
        const searchMatch = searchTerm.trim() === '' || 
                            faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
        
        return categoryMatch && searchMatch;
      })
      .sort((a, b) => a.question.localeCompare(b.question)); // Sort alphabetically
  }, [searchTerm, selectedCategory]);

  const toggleFAQ = (id: number) => {
    setOpenFAQId(openFAQId === id ? null : id);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      
      {/* Header Banner - Similar look to your image_ef3bdc.png */}
      <div className="bg-white py-12 px-4 sm:px-6 lg:px-8 shadow-md">
          <div className="max-w-8xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between">
              
              <div className="lg:w-1/2 mb-6 lg:mb-0">
                <h1 className="text-4xl font-extrabold text-gray-800 mb-4">
                  Frequently Asked Questions
                </h1>
                <p className="text-lg text-gray-600">
                  We know what our customers ask us many time. By searching this part, you can find almost solutions you need to solve.
                </p>
                
                {/* Search Bar and Category Dropdown */}
                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                    
                    {/* Search Input Field */}
                    <input
                      type="text"
                      placeholder="Search any query, e.g., 'delivery', 'payment', 'track order'"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-red-500 focus:border-red-500 transition duration-150"
                    />

                    {/* Category Select Dropdown */}
                    <div className="sm:w-1/3">
                        <select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 bg-white rounded-lg shadow-sm text-gray-700 focus:ring-red-500 focus:border-red-500 transition duration-150"
                        >
                          <option value="All">Select Category</option>
                          {categories.filter(c => c !== 'All').map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                    </div>
                </div>

              </div>
              
              {/* Image Illustration - Replaced placeholder with a real image */}
              <div className="lg:w-2/5 flex justify-center lg:justify-end">
                 {/* - Added a relevant image */}
                 <img src="https://freepngimg.com/thumb/graphic_design/88516-blue-product-faq-question-mark-free-clipart-hd.png" alt="FAQ Illustration" className="max-w-full h-auto  " />
              </div>
          </div>
      </div>
      
      {/* --- FAQ List Section --- */}
      <div className="max-w-8xl mx-auto p-4 sm:p-6 lg:p-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b pb-2">
            Frequently Asked Questions:
        </h2>

       <div className="space-y-4"> {/* gap between each card */}
  {filteredFAQs.length > 0 ? (
    filteredFAQs.map((faq) => (
      <div
        key={faq.id}
        className="bg-white rounded-xl shadow-md p-4 transition-all duration-200 hover:shadow-lg"
      >
        <FAQAccordionItem
          faq={faq}
          isActive={openFAQId === faq.id}
          onClick={() => toggleFAQ(faq.id)}
        />
      </div>
    ))
  ) : (
    <div className="p-8 text-center text-gray-500 text-lg bg-white rounded-xl shadow-sm">
      "No questions found. Change your search or select a different category."
    </div>
  )}
</div>

        
      </div>
    </div>
  );
};

export default FAQPage;